import { memo, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { Civil } from './date-utils';
import type { TimeVariant } from './types';

interface TimePickerProps {
  value: Civil;
  onChange: (next: Civil) => void;
  use12Hour?: boolean;
  showSeconds?: boolean;
  minuteStep?: number;
  variant?: TimeVariant;
  disabled?: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');

function clampWrap(n: number, max: number): number {
  // max is exclusive-count (e.g. 60 for minutes, 24 for hours)
  return ((n % max) + max) % max;
}

/**
 * Time selector rendered beneath the calendar for datetime modes. Three input
 * styles share the same value contract:
 *  - `stepper`  compact numeric field with ▲▼ buttons (default)
 *  - `dropdown` native selects (great on touch / for coarse minute steps)
 *  - `input`    plain typed fields
 */
export const TimePicker = memo(function TimePicker({
  value,
  onChange,
  use12Hour = false,
  showSeconds = false,
  minuteStep = 1,
  variant = 'stepper',
  disabled,
}: TimePickerProps) {
  const isPM = value.hour >= 12;
  const displayHour = use12Hour ? (value.hour % 12 === 0 ? 12 : value.hour % 12) : value.hour;

  const setHour24 = (h24: number) => onChange({ ...value, hour: clampWrap(h24, 24) });
  const setDisplayHour = (h: number) => {
    if (use12Hour) {
      const base = clampWrap(h - 1, 12) + 1; // 1..12
      const h24 = (base % 12) + (isPM ? 12 : 0);
      onChange({ ...value, hour: h24 });
    } else {
      setHour24(h);
    }
  };
  const setMinute = (m: number) => onChange({ ...value, minute: clampWrap(m, 60) });
  const setSecond = (s: number) => onChange({ ...value, second: clampWrap(s, 60) });
  const toggleMeridiem = () => onChange({ ...value, hour: clampWrap(value.hour + 12, 24) });

  const minuteOptions = useMemo(() => {
    const step = Math.max(1, minuteStep);
    return Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  }, [minuteStep]);

  if (variant === 'dropdown') {
    const selectCls =
      'h-9 rounded-lg border border-input bg-background px-2 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50';
    const hourList = use12Hour ? Array.from({ length: 12 }, (_, i) => i + 1) : Array.from({ length: 24 }, (_, i) => i);
    return (
      <div className="flex items-center gap-2" role="group" aria-label="Time">
        <select
          className={selectCls}
          value={displayHour}
          disabled={disabled}
          onChange={(e) => setDisplayHour(Number(e.target.value))}
          aria-label="Hour"
        >
          {hourList.map((h) => (
            <option key={h} value={h}>
              {pad(h)}
            </option>
          ))}
        </select>
        <span className="text-muted-foreground">:</span>
        <select
          className={selectCls}
          value={value.minute - (value.minute % Math.max(1, minuteStep))}
          disabled={disabled}
          onChange={(e) => setMinute(Number(e.target.value))}
          aria-label="Minute"
        >
          {minuteOptions.map((m) => (
            <option key={m} value={m}>
              {pad(m)}
            </option>
          ))}
        </select>
        {showSeconds && (
          <>
            <span className="text-muted-foreground">:</span>
            <select
              className={selectCls}
              value={value.second}
              disabled={disabled}
              onChange={(e) => setSecond(Number(e.target.value))}
              aria-label="Second"
            >
              {Array.from({ length: 60 }, (_, i) => i).map((s) => (
                <option key={s} value={s}>
                  {pad(s)}
                </option>
              ))}
            </select>
          </>
        )}
        {use12Hour && (
          <button
            type="button"
            onClick={toggleMeridiem}
            disabled={disabled}
            className="h-9 rounded-lg border border-input px-3 text-xs font-medium hover:bg-muted disabled:opacity-50"
          >
            {isPM ? 'PM' : 'AM'}
          </button>
        )}
      </div>
    );
  }

  // stepper / input share the field styling; stepper adds ▲▼ controls.
  const field = (label: string, val: number, set: (n: number) => void, step = 1) => (
    <div className="flex flex-col items-center">
      {variant === 'stepper' && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Increase ${label}`}
          disabled={disabled}
          onClick={() => set(val + step)}
          className="flex h-5 w-full items-center justify-center rounded-t-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
      )}
      <input
        type="text"
        inputMode="numeric"
        aria-label={label}
        disabled={disabled}
        value={pad(val)}
        onChange={(e) => {
          const n = Number(e.target.value.replace(/\D/g, ''));
          if (!Number.isNaN(n)) set(n);
        }}
        className="h-9 w-11 border border-input bg-background text-center text-xs tabular-nums outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50"
      />
      {variant === 'stepper' && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Decrease ${label}`}
          disabled={disabled}
          onClick={() => set(val - step)}
          className="flex h-5 w-full items-center justify-center rounded-b-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="flex items-center gap-1.5" role="group" aria-label="Time">
      {field('Hour', displayHour, setDisplayHour)}
      <span className="pb-0.5 text-muted-foreground">:</span>
      {field('Minute', value.minute, setMinute, Math.max(1, minuteStep))}
      {showSeconds && (
        <>
          <span className="pb-0.5 text-muted-foreground">:</span>
          {field('Second', value.second, setSecond)}
        </>
      )}
      {use12Hour && (
        <button
          type="button"
          onClick={toggleMeridiem}
          disabled={disabled}
          className="ml-1 h-9 rounded-lg border border-input px-3 text-xs font-medium hover:bg-muted disabled:opacity-50"
        >
          {isPM ? 'PM' : 'AM'}
        </button>
      )}
    </div>
  );
});
